import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, TrendingUp, Users, DollarSign, Sparkles } from 'lucide-react';

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Animated Background Waves */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="wave-animation wave-1"></div>
        <div className="wave-animation wave-2"></div>
        <div className="wave-animation wave-3"></div>
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a2e]/50 to-[#0a0a2e]"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="text-center space-y-8 mb-16">
          {/* Badge */}
          <div className="inline-block">
            <span className="px-6 py-3 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/40 text-purple-300 text-sm font-bold backdrop-blur-sm animate-pulse flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Revolutionary Creator Economy Platform
            </span>
          </div>
          
          {/* Main Headline */}
          <h1 className="text-5xl lg:text-7xl xl:text-8xl font-bold leading-tight">
            <span className="text-white">Your Fans Can Now</span>
            <br />
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-500 bg-clip-text text-transparent animate-gradient">
              Invest in You
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-2xl lg:text-3xl text-gray-300 leading-relaxed max-w-4xl mx-auto font-light">
            Turn your biggest fans into invested partners. They share in your revenue as you grow,
            <span className="text-cyan-400 font-semibold"> creating sustainable income</span> while you
            <span className="text-pink-400 font-semibold"> protect your content</span>.
          </p>

          {/* Stats Row */}
          <div className="flex flex-wrap justify-center gap-8 pt-8">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <DollarSign className="w-8 h-8 text-cyan-400" />
                <p className="text-5xl font-bold text-cyan-400">£50K+</p>
              </div>
              <p className="text-gray-400">Average Raised</p>
            </div>
            <div className="h-16 w-px bg-gray-700"></div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Users className="w-8 h-8 text-purple-400" />
                <p className="text-5xl font-bold text-purple-400">250+</p>
              </div>
              <p className="text-gray-400">Active Investors</p>
            </div>
            <div className="h-16 w-px bg-gray-700"></div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <TrendingUp className="w-8 h-8 text-pink-400" />
                <p className="text-5xl font-bold text-pink-400">3x</p>
              </div>
              <p className="text-gray-400">Revenue Growth</p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
            <Button 
              onClick={() => scrollToSection('early-access')}
              size="lg"
              className="bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 hover:from-purple-600 hover:via-pink-600 hover:to-cyan-600 text-white text-xl px-12 py-8 border-0 group shadow-2xl shadow-purple-500/50 animate-pulse"
            >
              Start Raising Funds Today
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={24} />
            </Button>
            <Button 
              onClick={() => scrollToSection('how-investment-works')}
              size="lg"
              variant="outline"
              className="border-2 border-cyan-500/50 hover:border-cyan-400 bg-transparent text-white text-xl px-12 py-8 group backdrop-blur-sm"
            >
              See How It Works
              <TrendingUp className="ml-2 group-hover:scale-110 transition-transform" size={24} />
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="pt-12 flex flex-wrap items-center justify-center gap-8 text-gray-400 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
              <span>100% Dispute Prevention</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
              <span>Secure Investment Platform</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-pink-400 animate-pulse"></div>
              <span>Transparent Revenue Sharing</span>
            </div>
          </div>
        </div>

        {/* Hero Visual */}
        <div className="relative max-w-5xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden border-2 border-purple-500/30 shadow-2xl shadow-purple-500/30 backdrop-blur-sm bg-gradient-to-br from-cyan-500/10 to-pink-500/10 p-8">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Investment Card */}
              <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 backdrop-blur-lg rounded-2xl p-6 border border-cyan-500/30 hover:scale-105 transition-transform">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-cyan-400 flex items-center justify-center text-white font-bold text-xl">
                    £
                  </div>
                  <div>
                    <p className="text-cyan-400 font-bold text-lg">Fan Invests</p>
                    <p className="text-white text-2xl font-bold">£100</p>
                  </div>
                </div>
                <p className="text-gray-300 text-sm">Sarah becomes an investor in your channel</p>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <ArrowRight className="w-12 h-12 text-purple-400 animate-pulse" />
              </div>

              {/* Revenue Share Card */}
              <div className="bg-gradient-to-br from-pink-500/20 to-purple-500/20 backdrop-blur-lg rounded-2xl p-6 border border-pink-500/30 hover:scale-105 transition-transform">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-pink-400 flex items-center justify-center text-white font-bold text-xl">
                    ↑
                  </div>
                  <div>
                    <p className="text-pink-400 font-bold text-lg">Monthly Return</p>
                    <p className="text-white text-2xl font-bold">£15</p>
                  </div>
                </div>
                <p className="text-gray-300 text-sm">Sarah earns as your channel grows</p>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-cyan-500/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-pink-500/20 rounded-full blur-3xl"></div>
          </div>

          {/* Floating Testimonial */}
          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-full max-w-2xl">
            <div className="bg-gradient-to-r from-gray-900/95 to-gray-800/95 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50 shadow-2xl">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-cyan-400 to-pink-500 flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
                  JD
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium mb-1">"Raised £45,000 from my community in 2 months!"</p>
                  <p className="text-gray-400 text-sm">- James, Gaming Creator with 50K subscribers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
